<template>
    <v-container class="accounts-container mb-12 mb-lg-0">
        <v-row justify="center">
            <v-col cols="12">
                <v-card width="100%">
                    <v-card-title class="primary secondary--text d-flex flex-wrap align-center pa-2" style="word-break: normal">
                        <v-icon color="secondary" class="mr-2">mdi-account-cash</v-icon>
                        <span class="font-weight-bold">MIS CUENTAS</span>
                        <v-spacer/>
                        <v-btn color="secondary" class="primary--text" depressed small @click="getData(true)">
                            <v-icon left small>mdi-sync</v-icon>Actualizar
                        </v-btn>
                    </v-card-title>
                </v-card>
            </v-col>
        </v-row>
        <v-row justify="center">
            <v-col cols="12">
                <v-card>
                    <v-card-title class="accounts-section-title d-flex flex-column flex-sm-row align-start align-sm-center">
                        <div class="d-flex align-center min-width-0">
                            <v-icon color="primary" class="mr-2">mdi-bank-check</v-icon>
                            <span class="subtitle-1 font-weight-bold primary--text">Cuentas propias</span>
                        </div>
                        <v-spacer class="d-none d-sm-flex"/>
                        <v-btn color="primary" depressed class="mt-3 mt-sm-0" :block="$vuetify.breakpoint.xsOnly" @click="openDialog">
                            <v-icon left small>mdi-plus-circle</v-icon>Agregar cuenta
                        </v-btn>
                    </v-card-title>
                    <v-divider/>
                    <v-card-text class="accounts-list-body">
                        <v-skeleton-loader v-if="loading" type="article, article"/>
                        <template v-else-if="accounts.length">
                            <v-row dense>
                                <v-col
                                    v-for="item in accounts"
                                    :key="'account-card-' + item.id"
                                    cols="12"
                                    md="6"
                                    xl="4">
                                    <v-sheet outlined rounded class="account-card pa-3 fill-height">
                                        <div class="account-card-header">
                                            <v-avatar color="secondary" size="44" class="account-card-avatar">
                                                <v-icon color="primary">mdi-bank</v-icon>
                                            </v-avatar>
                                            <div class="account-card-main">
                                                <div class="subtitle-1 font-weight-bold primary--text account-card-title">
                                                    {{ item.bank_name }}
                                                </div>
                                                <div class="body-2 grey--text text--darken-1 account-card-subtitle">
                                                    {{ item.account_type }} {{ item.account_mask }}
                                                </div>
                                            </div>
                                            <v-chip small dark :color="statusColor(item.status)" class="account-status-chip">
                                                {{ item.status_label }}
                                            </v-chip>
                                        </div>
                                        <v-divider class="my-3"/>
                                        <div class="account-detail-grid">
                                            <div class="account-detail">
                                                <div class="caption text-uppercase font-weight-bold primary--text">Titular</div>
                                                <div class="body-2 account-detail-value">{{ item.holder_name }}</div>
                                            </div>
                                            <div class="account-detail">
                                                <div class="caption text-uppercase font-weight-bold primary--text">Pais</div>
                                                <div class="body-2 account-detail-value">{{ countryLabel(item.country) }}</div>
                                            </div>
                                            <div class="account-detail" v-if="item.holder_document">
                                                <div class="caption text-uppercase font-weight-bold primary--text">Documento</div>
                                                <div class="body-2 account-detail-value">{{ item.holder_document }}</div>
                                            </div>
                                            <div class="account-detail">
                                                <div class="caption text-uppercase font-weight-bold primary--text">Registrada</div>
                                                <div class="body-2 account-detail-value">{{ item.created_at }}</div>
                                            </div>
                                        </div>
                                        <div v-if="item.review_note" class="account-note mt-3 pa-2">
                                            <div class="caption text-uppercase font-weight-bold primary--text">Nota</div>
                                            <div class="body-2 account-detail-value">{{ item.review_note }}</div>
                                        </div>
                                        <div class="account-card-actions mt-3">
                                            <v-btn
                                                v-if="item.certificate_url"
                                                small
                                                outlined
                                                color="primary"
                                                :href="item.certificate_url"
                                                target="_blank">
                                                <v-icon left small>mdi-file-eye</v-icon>Ver certificado
                                            </v-btn>
                                        </div>
                                    </v-sheet>
                                </v-col>
                            </v-row>
                        </template>
                        <v-sheet v-else outlined rounded class="pa-4 text-center">
                            <v-icon color="primary" large>mdi-bank-off</v-icon>
                            <div class="subtitle-2 primary--text font-weight-bold mt-2">
                                No tienes cuentas registradas
                            </div>
                        </v-sheet>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>

        <v-dialog
            v-model="dialog"
            persistent
            scrollable
            max-width="920px"
            :fullscreen="$vuetify.breakpoint.xsOnly"
            :hide-overlay="$vuetify.breakpoint.xsOnly">
            <v-form ref="form" v-model="valid" @submit.prevent="submit" lazy-validation>
                <v-card class="accounts-fixed-dialog">
                    <v-progress-linear indeterminate fixed v-if="loading" style="top:0"/>
                    <v-card-title class="primary secondary--text d-flex align-center accounts-fixed-header" style="word-break: normal">
                        <v-icon color="secondary" class="mr-2">mdi-bank-plus</v-icon>
                        Registrar cuenta
                        <v-spacer/>
                        <v-btn icon color="secondary" :disabled="loading" @click="closeDialog">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-card-text class="accounts-fixed-body pt-4">
                        <v-sheet outlined rounded class="pa-3 mb-4 account-holder-sheet">
                            <div class="caption text-uppercase font-weight-bold primary--text">Titular permitido</div>
                            <div class="subtitle-2 font-weight-bold">{{ holderName }}</div>
                        </v-sheet>
                        <v-row>
                            <v-col cols="12" v-if="identityDocuments.length">
                                <v-select
                                    v-model="form.identity_document_id"
                                    :items="identityDocuments"
                                    label="Documento aprobado"
                                    item-text="label"
                                    item-value="id"
                                    prepend-inner-icon="mdi-card-account-details-star"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    :rules="[rules.required('identity_document_id')]"
                                    :error-messages="errors.identity_document_id"
                                    :disabled="loading"
                                    @change="clearErrors('identity_document_id')"/>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="form.country"
                                    :items="countries"
                                    label="Pais"
                                    item-text="name"
                                    item-value="code"
                                    prepend-inner-icon="mdi-map-marker"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    :rules="[rules.required('country')]"
                                    :error-messages="errors.country"
                                    :disabled="loading"
                                    @change="countryChanged"/>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="form.bank_code"
                                    :items="banks"
                                    label="Banco"
                                    item-text="name"
                                    item-value="code"
                                    prepend-inner-icon="mdi-bank"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    :rules="[rules.required('bank_code')]"
                                    :error-messages="errors.bank_code"
                                    :disabled="loading || !form.country"
                                    @change="clearErrors('bank_code')"/>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select
                                    v-model="form.account_type"
                                    :items="accountTypes"
                                    label="Tipo de cuenta"
                                    prepend-inner-icon="mdi-bank-transfer"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    :rules="[rules.required('account_type')]"
                                    :error-messages="errors.account_type"
                                    :disabled="loading"
                                    @change="clearErrors('account_type')"/>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="form.account_number"
                                    label="Numero de cuenta"
                                    prepend-inner-icon="mdi-pound"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    :counter="80"
                                    :rules="[rules.required('account_number'), rules.min('account_number', 4, 'El'), rules.max('account_number', 80, 'El')]"
                                    :error-messages="errors.account_number"
                                    :disabled="loading"
                                    @input="clearErrors('account_number')"/>
                            </v-col>
                            <v-col cols="12">
                                <v-file-input
                                    v-model="form.certificate"
                                    label="Certificado bancario"
                                    accept="image/png,image/jpeg,application/pdf"
                                    prepend-inner-icon="mdi-file-certificate"
                                    dense
                                    outlined
                                    hide-details="auto"
                                    :rules="[rules.required('certificate')]"
                                    :error-messages="errors.certificate"
                                    :disabled="loading"
                                    show-size
                                    counter
                                    @change="clearErrors('certificate')"/>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-divider/>
                    <v-card-actions class="accounts-fixed-footer">
                        <v-btn color="error" text @click="closeDialog" :disabled="loading">
                            <v-icon left small>mdi-close-circle</v-icon>Cerrar
                        </v-btn>
                        <v-spacer class="d-none d-sm-flex"/>
                        <v-btn type="submit" color="success" :loading="loading" :disabled="loading || !valid">
                            <v-icon left small>mdi-check-circle</v-icon>Enviar a revision
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-form>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios';
import Form from '../helpers/form';

export default {
    name: "Accounts",
    mixins: [Form],
    props: ['user'],
    data: () => ({
        loading: true,
        valid: true,
        dialog: false,
        accounts: [],
        identityDocuments: [],
        banks: [],
        countries: [
            { name: 'Brasil', code: 'BRA' },
            { name: 'Chile', code: 'CHL' },
            { name: 'Colombia', code: 'COL' },
            { name: 'Ecuador', code: 'ECU' },
            { name: 'Estados Unidos', code: 'USA' },
            { name: 'Mexico', code: 'MEX' },
            { name: 'Panama', code: 'PAN' },
            { name: 'Peru', code: 'PER' },
            { name: 'Venezuela', code: 'VEN' }
        ],
        accountTypes: ['Ahorros', 'Corriente', 'Vista', 'Digital'],
        form: {
            country: null,
            bank_code: null,
            account_type: null,
            account_number: null,
            identity_document_id: null,
            certificate: null
        }
    }),
    computed: {
        holderName() {
            return (this.user.name + ' ' + (this.user.lname || '')).trim();
        }
    },
    created() {
        this.getData();
    },
    methods: {
        getData(reset) {
            this.loading = true;
            if (reset === true) {
                this.accounts = [];
            }

            axios.get('/api/customer-bank-accounts').then(res => {
                this.accounts = res.data.accounts || [];
                this.identityDocuments = res.data.identity_documents || [];
            }).catch(err => {
                this.$toast.error(this.errorMessage(err, 'No fue posible cargar tus cuentas.'), {icon: 'mdi-alert-circle'});
            }).then(() => {
                this.loading = false;
            });
        },
        openDialog() {
            this.dialog = true;
            this.form.country = this.user.country || null;
            this.form.identity_document_id = this.identityDocuments.length ? this.identityDocuments[0].id : null;
            if (this.form.country) {
                this.getBanks();
            }
        },
        closeDialog() {
            this.dialog = false;
            this.form = {
                country: null,
                bank_code: null,
                account_type: null,
                account_number: null,
                identity_document_id: null,
                certificate: null
            };
            this.banks = [];
            this.$refs.form.reset();
        },
        countryChanged() {
            this.form.bank_code = null;
            this.clearErrors('country');
            this.getBanks();
        },
        getBanks() {
            this.banks = [];
            if (!this.form.country) return;

            axios.get('/api/banks/' + this.form.country).then(res => {
                this.banks = (res.data.banks || []).filter(item => item.name !== 'Nuestras Oficinas');
            });
        },
        countryLabel(code) {
            const country = this.countries.find(item => item.code === code);

            return country ? country.name : (code || 'Sin registro');
        },
        submit() {
            if (!this.$refs.form.validate()) return;

            this.loading = true;
            const data = new FormData();
            data.append('country', this.form.country);
            data.append('bank_code', this.form.bank_code);
            data.append('account_type', this.form.account_type);
            data.append('account_number', this.form.account_number);
            if (this.form.identity_document_id) {
                data.append('identity_document_id', this.form.identity_document_id);
            }
            data.append('certificate', this.form.certificate);

            axios.post('/api/customer-bank-accounts', data, {
                headers: {'Content-Type': 'multipart/form-data'}
            }).then(res => {
                this.$toast.success(res.data.status, {icon: 'mdi-check-circle'});
                this.accounts = res.data.accounts || this.accounts;
                this.closeDialog();
            }).catch(err => {
                this.handleErrors(err.response && err.response.data ? err.response.data.errors : {});
                this.$toast.error(this.errorMessage(err, 'No fue posible registrar la cuenta.'), {icon: 'mdi-alert-circle'});
            }).then(() => {
                this.loading = false;
            });
        },
        statusColor(status) {
            if (status === 'approved') return 'success';
            if (status === 'rejected') return 'error';
            return 'warning';
        },
        errorMessage(err, fallback) {
            if (err.response && err.response.data && err.response.data.status) {
                return err.response.data.status;
            }

            return fallback;
        }
    }
}
</script>

<style scoped>
.accounts-container {
    max-width: 1320px;
}

.accounts-section-title {
    gap: 8px;
}

.accounts-list-body {
    padding: 16px;
}

.account-card,
.account-holder-sheet {
    border-color: rgba(13, 147, 165, .18) !important;
    border-radius: 8px;
}

.account-card-header {
    align-items: flex-start;
    display: flex;
    gap: 12px;
    min-width: 0;
}

.account-card-avatar,
.account-status-chip {
    flex: 0 0 auto;
}

.account-card-main {
    flex: 1 1 auto;
    min-width: 0;
}

.account-card-title,
.account-card-subtitle,
.account-detail-value {
    line-height: 1.2;
    word-break: normal;
    overflow-wrap: anywhere;
}

.account-status-chip {
    margin-left: auto;
}

.account-detail-grid {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.account-note {
    background: rgba(13, 147, 165, .06);
    border-radius: 8px;
}

.account-card-actions {
    display: flex;
    justify-content: flex-start;
}

.min-width-0 {
    min-width: 0;
}

.accounts-fixed-dialog {
    max-height: 90vh;
    display: flex;
    flex-direction: column;
}

.accounts-fixed-header,
.accounts-fixed-footer {
    flex: 0 0 auto;
}

.accounts-fixed-footer {
    gap: 8px;
    flex-wrap: wrap;
}

.accounts-fixed-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

@media (max-width: 600px) {
    .accounts-container {
        padding-left: 10px;
        padding-right: 10px;
    }

    .accounts-list-body {
        padding: 12px;
    }

    .account-card-header {
        flex-wrap: wrap;
        gap: 10px;
    }

    .account-status-chip {
        margin-left: 56px;
    }

    .account-detail-grid {
        grid-template-columns: 1fr;
    }

    .accounts-fixed-dialog {
        border-radius: 0 !important;
        height: 100%;
        max-height: 100vh;
    }

    .accounts-fixed-header {
        font-size: 1rem !important;
        line-height: 1.2;
        min-height: 56px;
        padding: 10px 14px !important;
    }

    .accounts-fixed-body {
        padding: 14px !important;
    }

    .accounts-fixed-footer {
        padding: 10px 14px calc(14px + env(safe-area-inset-bottom)) !important;
    }

    .accounts-fixed-footer .v-btn {
        flex: 1 1 calc(50% - 4px);
        min-width: 0;
    }
}
</style>
